import type { Session } from '@supabase/supabase-js';

import { clearAssetUrlCache } from '@/lib/asset-url-cache';
import { identifyUser, resetIdentity, track } from '@/lib/observability';
import { supabase } from '@/lib/supabase';

import { useAuthStore } from './auth.store';

/** Tag the session's user in Sentry + PostHog when present, clear it
 *  when not. Both telemetry calls are no-ops in local dev where the
 *  observability clients are uninitialised. */
const syncTelemetryIdentity = (session: Session | null): void => {
  if (session?.user?.id && session.user.email) {
    identifyUser({ id: session.user.id, email: session.user.email });
    return;
  }
  resetIdentity();
};

/**
 * Loads the initial session from Supabase and subscribes to auth-state
 * changes so the Zustand store stays in sync. Designed to be called once
 * from `main.tsx` before the React tree mounts; the returned function
 * unsubscribes (used during HMR teardown).
 */
export async function bootstrapAuth(): Promise<() => void> {
  const { setSession } = useAuthStore.getState();

  // Resolve the existing session (if any) from localStorage / cookies.
  const { data } = await supabase.auth.getSession();
  const initial = data.session ?? null;
  setSession(initial);
  syncTelemetryIdentity(initial);

  // Track null → user-id transitions, NOT every Supabase auth event.
  // Supabase fires SIGNED_IN on token refreshes, HMR re-mounts, and
  // INITIAL_SESSION restores in addition to actual sign-ins, so an
  // event-type check alone is too noisy. Seed from the resolved
  // `getSession` so a page reload while already signed in does not
  // fire a phantom track.
  let previousUserId: string | null = initial?.user?.id ?? null;

  // Then keep the store live with later auth events: sign-in, sign-out,
  // token refreshes, magic-link redirects. On sign-out drop any cached
  // signed URLs so the next user doesn't reuse the previous one's.
  const { data: subscription } = supabase.auth.onAuthStateChange(
    (event, session) => {
      useAuthStore.getState().setSession(session ?? null);
      syncTelemetryIdentity(session ?? null);

      const currentUserId = session?.user?.id ?? null;
      if (previousUserId === null && currentUserId !== null) {
        track('auth_signed_in', { user_id: currentUserId });
      }
      previousUserId = currentUserId;

      if (event === 'SIGNED_OUT') {
        clearAssetUrlCache();
      }
    },
  );

  return () => subscription.subscription.unsubscribe();
}
